/* global GLOBAL_CONFIG -- injected global configuration */
import { CMSDialog, CMSNotification, useDialogStore, useNotificationStore, useSocketConnected, useSockets } from '@editor/ui-utils';
import type { Socket } from 'socket.io-client';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

interface IServerToClientEventData {
  serverVersion: string;
  forceUpdate: boolean;
  softUpdate: boolean;
}

interface IServerToClientEvents {
  handshakeReply: (handshake: IServerToClientEventData) => void;
}

interface IClientToServerEvents {
  handshake: (clientVersion: string) => void;
}

enum UpdateInfoType {
  FORCE_UPDATE = 'FORCE_UPDATE',
  SOFT_UPDATE = 'SOFT_UPDATE',
}

export default function usePing(): void {
  let lastNotifiedVersion: string;
  let hasForceUpdateTakenOver = false;

  const router = useRouter();
  const { t } = useI18n();
  const { showDialog, isDialogVisible } = useDialogStore();
  const { showNotification, isNotificationVisible, hideNotification } = useNotificationStore();

  const forcedReloadDialog = new CMSDialog(
    {
      message: t('pingApi.forceUpdate'),
      ok: t('pingApi.reload'),
      cancel: false,
      persistent: true,
    },
    UpdateInfoType.FORCE_UPDATE,
  );
  const softReloadNotification = new CMSNotification(t('pingApi.softUpdate'), 'info', {
    id: UpdateInfoType.SOFT_UPDATE,
    color: 'purple',
    timeout: CMSNotification.NO_TIMEOUT,
    dismissible: false,
    callbackLabel: t('pingApi.reload'),
    callbackDataQa: 'reload',
    badgeStyle: { display: 'none' },
    async callbackFunc() {
      window.location.reload();
    },
  });

  const handleSocketHandshakeReply = async ({ serverVersion, forceUpdate, softUpdate }: IServerToClientEventData): Promise<void> => {
    // Exit early if there's no action required (force update dialog already visible)
    if (hasForceUpdateTakenOver) return;

    // Exit early if there's no action required (versions match and no force update)
    if (lastNotifiedVersion === serverVersion && !forceUpdate) return;

    // if force update dialog is open, there is no need for any other action than force user to reload
    const isForceUpdateDialogOpen = isDialogVisible(UpdateInfoType.FORCE_UPDATE);
    if (isForceUpdateDialogOpen) return;

    // if soft update notification is visible and another message with soft update came from server there is also nothing to do
    const isSoftUpdateNotificationVisible = isNotificationVisible(UpdateInfoType.SOFT_UPDATE);
    if (isSoftUpdateNotificationVisible && softUpdate) return;

    if (forceUpdate) {
      hasForceUpdateTakenOver = true; // mark as forced to update, either by updated soft update dialog or new force update dialog

      // if force update message came while soft update notification is visible, hide now irrelevant soft update notification
      if (isSoftUpdateNotificationVisible) {
        hideNotification(UpdateInfoType.SOFT_UPDATE);
      }

      showDialog(forcedReloadDialog).onDismiss(() => window.location.reload());
      lastNotifiedVersion = serverVersion;
      return;
    }

    if (softUpdate) {
      showNotification(softReloadNotification);
      router.afterEach(() => {
        showNotification(softReloadNotification);
      });
      lastNotifiedVersion = serverVersion;
    }
  };

  const handleSocketResponse = (isConnected?: boolean): void => {
    if (!isConnected) return;
    const io = useSockets();
    const pingChannel: Socket<IServerToClientEvents, IClientToServerEvents> = io.socket('/ping');
    pingChannel.on('handshakeReply', handleSocketHandshakeReply);
    pingChannel.on('connect', () => {
      pingChannel.emit('handshake', GLOBAL_CONFIG.VERSION);
    });
  };

  watch(useSocketConnected(), handleSocketResponse, { immediate: true });
}
